import React from 'react';
import styled, { keyframes, css } from 'styled-components';

import visaLogo from '../assets/img/icon/visa.png'; 
import cbLogo from '../assets/img/icon/cb.png'; 
import mastercardLogo from '../assets/img/icon/mastercard.png'; 

const flip = keyframes`
  0% { transform: rotateY(0); }
  100% { transform: rotateY(180deg); }
`;

const unflip = keyframes`
  0% { transform: rotateY(180deg); }
  100% { transform: rotateY(0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(0, 170, 255, 0.5); }
  50% { box-shadow: 0 0 15px rgba(0, 170, 255, 0.8); }
  100% { box-shadow: 0 0 5px rgba(0, 170, 255, 0.5); }
`;

// Effet de reflet subtil sur la carte
const shimmer = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

const CardContainer = styled.div`
  perspective: 1000px;
  width: 340px;
  height: 210px;
  margin: 1rem auto;
`;

// Utilisation du préfixe $ pour rendre la prop "transient" (non transmise au DOM)
const CardInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  animation: ${props => props.$flipped ? flip : unflip} 0.6s forwards;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  backface-visibility: hidden;
  padding: 1.5rem;
  box-sizing: border-box;
  background: linear-gradient(135deg, #103f58, #215872);
  color: #fff;
  font-family: 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
  
  // Effet de brillance subtil pour un aspect plus premium
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0) 0%,
      rgba(255,255,255,0.15) 50%,
      rgba(255,255,255,0) 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 5s infinite linear;
    pointer-events: none;
    z-index: 2;
  }
`;

const CardFront = styled(CardFace)`
  z-index: 1;
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  background: linear-gradient(135deg, #1b1b1b, #2e2e2e);
`;

// Puce de carte améliorée avec effet métallique
const Chip = styled.div`
  width: 50px;
  height: 35px;
  background: linear-gradient(135deg, #d0a84c, #f7df8b, #d0a84c);
  border-radius: 6px;
  position: relative;
  box-shadow: 0 1px 2px rgba(0,0,0,0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    width: 34px;
    height: 19px;
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255,255,255,0.1) 0%,
      rgba(255,255,255,0.3) 50%,
      rgba(255,255,255,0.1) 100%
    );
    border-radius: 6px;
  }
`;

// Également modifier les autres props avec le préfixe $
const CardNumber = styled.div`
  font-size: 1.3rem;
  letter-spacing: 2px;
  margin-top: 1.5rem;
  padding: 0.3rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  text-shadow: 
    0px 1px 0px rgba(255,255,255,0.2),
    0px -1px 0px rgba(0,0,0,0.7);
  
  // Effet d'embossage subtil
  transform: translateZ(0.5px);
  
  ${props => props.$focused && css`
    background-color: rgba(0, 170, 255, 0.2);
    animation: ${glow} 1.5s infinite;
    border: 1px solid rgba(0, 170, 255, 0.6);
  `}
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  text-shadow: 0 1px 1px rgba(0,0,0,0.4);
  margin-top: auto;
`;

const CardHolder = styled.div`
  padding: 0.3rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  ${props => props.$focused && css`
    background-color: rgba(0, 170, 255, 0.2);
    animation: ${glow} 1.5s infinite;
    border: 1px solid rgba(0, 170, 255, 0.6);
  `}
  
  div {
    font-weight: bold;
    text-shadow: 
      0px 1px 0px rgba(255,255,255,0.2),
      0px -1px 0px rgba(0,0,0,0.7);
    transform: translateZ(0.5px);
  }
`;

const CardExpiry = styled.div`
  text-align: right;
  padding: 0.3rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  ${props => props.$focused && css`
    background-color: rgba(0, 170, 255, 0.2);
    animation: ${glow} 1.5s infinite;
    border: 1px solid rgba(0, 170, 255, 0.6);
  `}
  
  div {
    font-weight: bold;
    text-shadow: 
      0px 1px 0px rgba(255,255,255,0.2),
      0px -1px 0px rgba(0,0,0,0.7);
    transform: translateZ(0.5px);
  }
`;

const CardLabel = styled.div`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CVCLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
`;

const CVCBox = styled.div`
  background: #eee;
  color: #000;
  text-align: right;
  padding: 0.5rem;
  border-radius: 4px;
  font-weight: bold;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.3);
  
  ${props => props.$focused && css`
    background-color: rgba(255, 255, 255, 0.9);
    animation: ${glow} 1.5s infinite;
    border: 1px solid rgba(0, 170, 255, 0.6);
  `}
`;

const Logo = styled.img`
  width: 60px;
  height: auto;
  position: absolute;
  top: 0.5rem;
  right: 1.5rem;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.3));
`;

const brandLogos = {
    visa: visaLogo,
    cb: cbLogo,
    mastercard: mastercardLogo,
    amex: 'https://cdn.iconscout.com/icon/free/png-256/free-american-express-2-730225.png',
    default: cbLogo
};

const CreditCardVisual = ({
    cardNumber = '',
    holder = 'NOM PRÉNOM',
    expiry = 'MM/AA',
    cvc = '',
    focused = '',
    brand = 'default'
}) => {
    const isFlipped = focused === 'cvc';

    // Normalisation du brand
    const normalizedBrand = brand ? brand.toLowerCase() : 'default';
    const logoSrc = brandLogos[normalizedBrand] || brandLogos.default;

    return (
        <CardContainer>
            {/* Utilisez $flipped au lieu de isFlipped */}
            <CardInner $flipped={isFlipped}>
                <CardFront>
                    <Chip />
                    {/* Utilisez $focused au lieu de isFocused */}
                    <CardNumber $focused={focused === 'number'}>
                        {cardNumber || '•••• •••• •••• ••••'}
                    </CardNumber>

                    <CardRow>
                        <CardHolder $focused={focused === 'name'}>
                            <CardLabel>Titulaire</CardLabel>
                            <div>{holder}</div>
                        </CardHolder>

                        <CardExpiry $focused={focused === 'expiry'}>
                            <CardLabel>Expiration</CardLabel>
                            <div>{expiry}</div>
                        </CardExpiry>
                    </CardRow>

                    <Logo
                        src={logoSrc}
                        alt={brand || 'card'}
                        onError={(e) => {
                            e.target.src = brandLogos.default;
                        }}
                    />
                </CardFront>

                <CardBack>
                    <CVCLabel>Code de sécurité</CVCLabel>
                    <CVCBox $focused={focused === 'cvc'}>
                        {cvc || '•••'}
                    </CVCBox>
                </CardBack>
            </CardInner>
        </CardContainer>
    );
};

export default CreditCardVisual;